import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import {
    BulkOrganizationTaskTemplateAction,
    useBulkArchiveOrganizationTaskTemplatesMutation,
    useBulkDeleteOrganizationTaskTemplatesMutation,
    useBulkDetachOrganizationTaskTemplatesMutation,
    useBulkUnarchiveOrganizationTaskTemplatesMutation,
} from "@api/organizationTaskTemplate.api";

type BulkMutation = ReturnType<
    typeof useBulkArchiveOrganizationTaskTemplatesMutation
>;

const BULK_ACTION_COPY: Record<
    BulkOrganizationTaskTemplateAction,
    { pastTense: string; failureMessage: string }
> = {
    archive: {
        pastTense: "Archived",
        failureMessage: "Failed to archive task templates",
    },
    unarchive: {
        pastTense: "Enabled",
        failureMessage: "Failed to enable task templates",
    },
    detach: {
        pastTense: "Detached",
        failureMessage: "Failed to detach task templates",
    },
    delete: {
        pastTense: "Deleted",
        failureMessage: "Failed to delete task templates",
    },
};

interface Params {
    selectedTemplates: OrganizationTaskTemplate[];
    serviceTemplateCounts: Readonly<Record<string, number>>;
    onClearSelection: () => void;
}

export function useOrganizationTaskTemplateBulkActions({
    selectedTemplates,
    serviceTemplateCounts,
    onClearSelection,
}: Params) {
    const { toast: successToast, error: errorToast } = useToaster();
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
        useState(false);
    const [isDetachConfirmationOpen, setIsDetachConfirmationOpen] =
        useState(false);

    const archiveMutation = useBulkArchiveOrganizationTaskTemplatesMutation();
    const unarchiveMutation =
        useBulkUnarchiveOrganizationTaskTemplatesMutation();
    const detachMutation = useBulkDetachOrganizationTaskTemplatesMutation();
    const deleteMutation = useBulkDeleteOrganizationTaskTemplatesMutation();

    const archivableIds = useMemo(
        () =>
            selectedTemplates
                .filter((template) => template.archivedAt == null)
                .map((template) => template.id),
        [selectedTemplates],
    );
    const enablableIds = useMemo(
        () =>
            selectedTemplates
                .filter((template) => template.archivedAt != null)
                .map((template) => template.id),
        [selectedTemplates],
    );
    const detachableIds = useMemo(
        () =>
            selectedTemplates
                .filter(
                    (template) => (serviceTemplateCounts[template.id] ?? 0) > 0,
                )
                .map((template) => template.id),
        [selectedTemplates, serviceTemplateCounts],
    );
    const selectedIds = useMemo(
        () => selectedTemplates.map((template) => template.id),
        [selectedTemplates],
    );

    const isBusy =
        archiveMutation.isPending ||
        unarchiveMutation.isPending ||
        detachMutation.isPending ||
        deleteMutation.isPending;

    const runBulkAction = useCallback(
        async ({
            action,
            mutation,
            templateIds,
        }: {
            action: BulkOrganizationTaskTemplateAction;
            mutation: BulkMutation;
            templateIds: string[];
        }): Promise<boolean> => {
            const { pastTense, failureMessage } = BULK_ACTION_COPY[action];
            closeCommands();
            try {
                const result = await mutation.mutateAsync(templateIds);
                const affectedLabel = `${pastTense} ${pluralizeWithCount("task template", result.affectedCount)}`;
                if (result.failedCount === 0) {
                    onClearSelection();
                    successToast(affectedLabel);
                } else if (result.affectedCount === 0) {
                    errorToast(failureMessage);
                    return false;
                } else {
                    errorToast(
                        `${affectedLabel}, ${result.failedCount} failed`,
                    );
                }
                return true;
            } catch {
                errorToast(failureMessage);
                return false;
            }
        },
        [closeCommands, onClearSelection, successToast, errorToast],
    );

    const handleArchive = useCallback(async () => {
        await runBulkAction({
            action: "archive",
            mutation: archiveMutation,
            templateIds: archivableIds,
        });
    }, [runBulkAction, archiveMutation, archivableIds]);

    const handleEnable = useCallback(async () => {
        await runBulkAction({
            action: "unarchive",
            mutation: unarchiveMutation,
            templateIds: enablableIds,
        });
    }, [runBulkAction, unarchiveMutation, enablableIds]);

    const handleConfirmDetach = useCallback(async () => {
        const isApplied = await runBulkAction({
            action: "detach",
            mutation: detachMutation,
            templateIds: detachableIds,
        });
        if (isApplied) {
            setIsDetachConfirmationOpen(false);
        }
    }, [runBulkAction, detachMutation, detachableIds]);

    const openDetachConfirmation = useCallback(() => {
        closeCommands();
        setIsDetachConfirmationOpen(true);
    }, [closeCommands]);
    const closeDetachConfirmation = useCallback(
        () => setIsDetachConfirmationOpen(false),
        [],
    );

    const handleConfirmDelete = useCallback(async () => {
        const isApplied = await runBulkAction({
            action: "delete",
            mutation: deleteMutation,
            templateIds: selectedIds,
        });
        if (isApplied) {
            setIsDeleteConfirmationOpen(false);
        }
    }, [runBulkAction, deleteMutation, selectedIds]);

    const openDeleteConfirmation = useCallback(() => {
        closeCommands();
        setIsDeleteConfirmationOpen(true);
    }, [closeCommands]);
    const closeDeleteConfirmation = useCallback(
        () => setIsDeleteConfirmationOpen(false),
        [],
    );

    const bulkModals = (
        <>
            <ConfirmationModal
                show={isDetachConfirmationOpen}
                title={`Detach ${pluralizeWithCount("task template", detachableIds.length)}?`}
                question="Service templates using the selected task templates will no longer be flagged when they fall out of sync with them."
                yes="Detach"
                no="Cancel"
                onReject={closeDetachConfirmation}
                onHide={closeDetachConfirmation}
                onConfirm={handleConfirmDetach}
            />
            <ConfirmationModal
                show={isDeleteConfirmationOpen}
                title={`Delete ${pluralizeWithCount("task template", selectedIds.length)}?`}
                question="The selected task templates will be permanently deleted."
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onReject={closeDeleteConfirmation}
                onHide={closeDeleteConfirmation}
                onConfirm={handleConfirmDelete}
            />
        </>
    );

    const handleOpenBulkActions = useCallback(() => {
        if (selectedIds.length === 0) {
            return;
        }
        openCommands(CommandsDirectory.ORGANIZATION_TASK_TEMPLATE_BULK, {
            selectedOrganizationTaskTemplateCount: selectedIds.length,
            onOrganizationTaskTemplateBulkArchive:
                archivableIds.length > 0
                    ? () => void handleArchive()
                    : undefined,
            onOrganizationTaskTemplateBulkEnable:
                enablableIds.length > 0 ? () => void handleEnable() : undefined,
            onOrganizationTaskTemplateBulkDetach:
                detachableIds.length > 0 ? openDetachConfirmation : undefined,
            onOrganizationTaskTemplateBulkDelete: openDeleteConfirmation,
        });
    }, [
        openCommands,
        selectedIds.length,
        archivableIds.length,
        enablableIds.length,
        detachableIds.length,
        handleArchive,
        handleEnable,
        openDetachConfirmation,
        openDeleteConfirmation,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedIds.length === 0) {
            return;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [selectedIds.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        isBusy,
        handleOpenBulkActions,
        bulkModals,
    };
}
